import React from 'react';
import Skeleton from 'react-loading-skeleton';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Empty, EmptyTable, HeaderCell, HeaderRow, SortLabel, TableBody, TableHead, Text, TooltipIcon, TooltipWrapper, User } from 'components';
import { theme } from 'design';
import { useWindowSize } from 'hooks';
import { ICurrentEmployee } from 'store/types';
import { EmployeeType, IEmployee, IPayee, ISiteRolePolicy } from 'types';
import { DEFAULT_SORT, formatJobTitle, isEmployeePayable } from 'utils';
import { isSortDesc, isSortedBy } from 'utils/table';
import { CellAction, IAddButtonProps, JobTitleCell, JobTitleHeaderCell, PayeeCell, StyledRow, StyledTable, TableButton } from './SelectPayeesTableStyles';

interface ITableProps {
  employees?: IEmployee[];
  payees?: IPayee[];
  isLoading?: boolean;
  onAddToPayeesList?: (id: string) => void;
  onSortChange?: (sort: string) => void;
  rolePolicy: ISiteRolePolicy;
  currentEmployee: ICurrentEmployee;
  urlQuery: IApiQueryOptions;
}

interface ISelectPayeeRowProps extends IAddButtonProps {
  employee?: IEmployee;
  isLoading?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

const DisabledWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  p {
    margin-right: 0;
  }
  svg {
    fill: ${theme.color.neutral[400]};
  }
`;

const SelectPayeeRow = ({ employee, isAdded, isLoading, onClick, disabled = false, ...rest }: ISelectPayeeRowProps) => {
  const isOrgAdmin = employee?.type === EmployeeType.ADMIN;
  const jobTitle = isOrgAdmin ? 'Org Admin' : formatJobTitle(employee?.sites?.find((site) => site?.job?.title)?.job?.title || '');
  const hasPaymentMethod = !!employee?.user?.card;

  // Query for displaying job title in User component
  // Aligns with css for hiding columns in table
  const windowSize = useWindowSize();
  const windowWidth = windowSize.width;
  const displayUserJobTitle = () => {
    if (windowWidth >= theme.breakpoint.xlarge || (windowWidth >= theme.breakpoint.med && windowWidth < theme.breakpoint.large)) return undefined;
    return jobTitle;
  };

  return (
    <StyledRow {...rest} id={`payee_${employee?.id}`} $isDisabled={disabled}>
      <PayeeCell data-label="Name" align="left" data-cy="name">
        <User stackable={false} id={employee?.id} username={employee?.name} role={displayUserJobTitle()} image={employee?.user?.avatarUrl} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} wrapText disabled={disabled} />
      </PayeeCell>

      <JobTitleCell data-label="Job Title" align="left">
        {isLoading ? <Skeleton width={77} /> : <Text size="xsmall">{jobTitle}</Text>}
      </JobTitleCell>

      <CellAction data-label="Add Employee" align="right">
        {disabled ? (
          <DisabledWrapper data-cy="ineligible-message">
            <Text size="xxsmall">Ineligible for tips</Text>
            <TooltipWrapper>
              <TooltipIcon id={`employee_tt_${employee?.id}`} place="left">
                This user is ineligible to receive tips
              </TooltipIcon>
            </TooltipWrapper>
          </DisabledWrapper>
        ) : (
          <TableButton
            data-cy="payee-add-btn"
            data-value={employee?.id}
            label={isAdded ? 'Added' : 'Add'}
            appearance="primary"
            size="small"
            isAdded={isAdded}
            onClick={onClick}
            isLoading={isLoading}
            disabled={disabled}
            icon="CheckmarkThick"
          />
        )}
      </CellAction>
    </StyledRow>
  );
};

const SelectPayeesTable: React.FC<ITableProps> = ({ employees, payees, isLoading, onAddToPayeesList, onSortChange, rolePolicy, currentEmployee, urlQuery }) => {
  const { limit = 25, sort = DEFAULT_SORT } = urlQuery || {};

  return (
    <>
      <StyledTable data-cy="select-payees-table">
        <TableHead>
          <HeaderRow bgColor="default">
            <HeaderCell align="left">
              <SortLabel label="Name" isSorted={isSortedBy('name', sort)} isSortedDesc={isSortDesc(sort)} onClick={() => onSortChange('name')} isLoading={isLoading} />
            </HeaderCell>
            <JobTitleHeaderCell align="left">{isLoading ? '' : 'Job Title'}</JobTitleHeaderCell>
            <HeaderCell />
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading
            ? [...Array(limit)].map((e, i) => <SelectPayeeRow key={i} employee={employees?.[i]} isLoading />)
            : employees &&
              employees.map((employee: IEmployee) => {
                const isAdded = payees?.some((payee: IPayee) => employee.id === payee.id);

                return (
                  <SelectPayeeRow
                    key={`payee_${employee.id}`}
                    employee={employee}
                    disabled={!isEmployeePayable(employee, rolePolicy, currentEmployee)}
                    isAdded={isAdded}
                    isLoading={isLoading}
                    data-cy="select-payees-row"
                    data-value={employee.id}
                    onClick={(e?: React.MouseEvent<HTMLElement>) => {
                      e?.stopPropagation();
                      onAddToPayeesList?.(employee.id);
                    }}
                  />
                );
              })}
          {!isLoading && isEmpty(employees) && (
            <EmptyTable>
              <Empty icon="People" message="No users found." />
            </EmptyTable>
          )}
        </TableBody>
      </StyledTable>
    </>
  );
};
export default SelectPayeesTable;
